import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  History,
  Search,
  Filter,
  Calendar,
  Clock,
  User,
  Monitor,
  StopCircle,
  Play,
  Download,
} from 'lucide-react'
import { formatDuration, formatRelativeTime } from '@/lib/utils'
import api from '@/lib/api'
import toast from 'react-hot-toast'

interface Session {
  id: string
  deviceId: string
  deviceName: string
  customerId: string
  customerName: string
  technicianId: string
  technicianName: string
  status: 'active' | 'completed' | 'terminated'
  startedAt: string
  endedAt: string | null
  duration: number
  notes: string | null
}

export default function SessionsPage() {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [dateFilter, setDateFilter] = useState<string>('all')
  const queryClient = useQueryClient()

  const { data: sessions } = useQuery<Session[]>({
    queryKey: ['sessions', statusFilter, dateFilter],
    queryFn: async () => {
      const response = await api.get('/sessions', {
        params: {
          status: statusFilter !== 'all' ? statusFilter : undefined,
          period: dateFilter !== 'all' ? dateFilter : undefined,
        },
      })
      return response.data
    },
    placeholderData: [
      {
        id: '1',
        deviceId: 'SC-4821-7593',
        deviceName: 'Sim Rig #1',
        customerId: '1',
        customerName: 'Speed Racing Center',
        technicianId: '1',
        technicianName: 'John Smith',
        status: 'active',
        startedAt: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
        endedAt: null,
        duration: 900,
        notes: null,
      },
      {
        id: '2',
        deviceId: 'SC-8294-1057',
        deviceName: 'Reception PC',
        customerId: '2',
        customerName: 'Downtown Sim Lounge',
        technicianId: '2',
        technicianName: 'Sarah Johnson',
        status: 'active',
        startedAt: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
        endedAt: null,
        duration: 2700,
        notes: null,
      },
      {
        id: '3',
        deviceId: 'SC-3847-2948',
        deviceName: 'Sim Rig #3',
        customerId: '3',
        customerName: 'ProSim Arena',
        technicianId: '1',
        technicianName: 'John Smith',
        status: 'completed',
        startedAt: new Date(Date.now() - 1000 * 60 * 180).toISOString(),
        endedAt: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
        duration: 3600,
        notes: 'Fixed iRacing configuration issue',
      },
      {
        id: '4',
        deviceId: 'SC-5739-8261',
        deviceName: 'Sim Rig #5',
        customerId: '2',
        customerName: 'Downtown Sim Lounge',
        technicianId: '3',
        technicianName: 'Mike Wilson',
        status: 'completed',
        startedAt: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(),
        endedAt: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
        duration: 3600,
        notes: 'Updated graphics drivers',
      },
      {
        id: '5',
        deviceId: 'SC-9182-4756',
        deviceName: 'Server Room PC',
        customerId: '3',
        customerName: 'ProSim Arena',
        technicianId: '2',
        technicianName: 'Sarah Johnson',
        status: 'terminated',
        startedAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
        endedAt: new Date(Date.now() - 1000 * 60 * 60 * 23).toISOString(),
        duration: 3600,
        notes: 'Connection lost - network issue on customer side',
      },
    ],
  })

  const filteredSessions = sessions?.filter((session) => {
    if (search) {
      const searchLower = search.toLowerCase()
      return (
        session.deviceName.toLowerCase().includes(searchLower) ||
        session.customerName.toLowerCase().includes(searchLower) ||
        session.technicianName.toLowerCase().includes(searchLower)
      )
    }
    return true
  })

  const activeSessions = filteredSessions?.filter((s) => s.status === 'active') || []
  const completedSessions = filteredSessions?.filter((s) => s.status !== 'active') || []
  const avgDurationSeconds = completedSessions.length > 0
    ? Math.round(completedSessions.reduce((sum, session) => sum + session.duration, 0) / completedSessions.length)
    : 0

  const exportCsv = useMemo(() => {
    const rows = [
      ['Device', 'Customer', 'Technician', 'Status', 'Started At', 'Ended At', 'Duration (seconds)', 'Notes'],
      ...(filteredSessions || []).map((session) => [
        session.deviceName,
        session.customerName,
        session.technicianName,
        session.status,
        session.startedAt,
        session.endedAt || '',
        String(session.duration),
        session.notes || '',
      ]),
    ]

    return rows
      .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
      .join('\n')
  }, [filteredSessions])

  const handleExport = () => {
    const blob = new Blob([exportCsv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `pitstop-sessions-${new Date().toISOString().slice(0, 10)}.csv`
    anchor.click()
    URL.revokeObjectURL(url)
    toast.success('Session report exported')
  }

  const endSessionMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      await api.post(`/sessions/${sessionId}/end`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      toast.success('Session ended')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to end session')
    },
  })

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="badge badge-busy">
            <span className="w-1.5 h-1.5 rounded-full bg-sc-accent-orange animate-pulse" />
            Active
          </span>
        )
      case 'completed':
        return (
          <span className="badge badge-online">
            <span className="w-1.5 h-1.5 rounded-full bg-sc-accent-green" />
            Completed
          </span>
        )
      case 'terminated':
        return (
          <span className="badge bg-sc-accent-red/10 text-sc-accent-red">
            <span className="w-1.5 h-1.5 rounded-full bg-sc-accent-red" />
            Terminated
          </span>
        )
      default:
        return null
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-sc-text-primary">Sessions</h1>
          <p className="text-sc-text-muted mt-1">
            View and manage remote support sessions
          </p>
        </div>
        <button className="btn-secondary" onClick={handleExport}>
          <Download className="w-4 h-4" />
          Export Report
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="stat-card">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-sc-accent-orange/10">
              <Play className="w-5 h-5 text-sc-accent-orange" />
            </div>
            <div>
              <p className="stat-value">{activeSessions.length}</p>
              <p className="stat-label">Active Sessions</p>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-sc-accent-green/10">
              <History className="w-5 h-5 text-sc-accent-green" />
            </div>
            <div>
              <p className="stat-value">{sessions?.length || 0}</p>
              <p className="stat-label">Total Today</p>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-sc-primary/10">
              <Clock className="w-5 h-5 text-sc-primary" />
            </div>
            <div>
              <p className="stat-value">{formatDuration(avgDurationSeconds)}</p>
              <p className="stat-label">Avg Duration</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sc-text-muted" />
              <input
                type="text"
                placeholder="Search sessions..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input pl-10"
              />
            </div>

            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-sc-text-muted" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input w-auto"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="terminated">Terminated</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-sc-text-muted" />
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="input w-auto"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Active Sessions */}
      {activeSessions.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2 className="font-semibold text-sc-text-primary flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-sc-accent-orange animate-pulse" />
              Active Sessions ({activeSessions.length})
            </h2>
          </div>
          <div className="divide-y divide-sc-border-dark">
            {activeSessions.map((session) => (
              <div key={session.id} className="p-4 hover:bg-sc-bg-medium/30 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-lg bg-sc-bg-light">
                      <Monitor className="w-5 h-5 text-sc-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sc-text-primary">
                          {session.deviceName}
                        </span>
                        {getStatusBadge(session.status)}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-sc-text-muted">
                        <span className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />
                          {session.technicianName}
                        </span>
                        <span>{session.customerName}</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {formatDuration(session.duration)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => endSessionMutation.mutate(session.id)}
                    disabled={endSessionMutation.isPending}
                    className="btn-danger py-1.5 px-3 text-xs"
                  >
                    <StopCircle className="w-3.5 h-3.5" />
                    {endSessionMutation.isPending ? 'Ending...' : 'End Session'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Session History */}
      <div className="card">
        <div className="card-header">
          <h2 className="font-semibold text-sc-text-primary">Session History</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Device</th>
                <th>Customer</th>
                <th>Technician</th>
                <th>Started</th>
                <th>Duration</th>
                <th>Status</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {completedSessions.map((session) => (
                <tr key={session.id}>
                  <td className="font-medium text-sc-text-primary">
                    {session.deviceName}
                  </td>
                  <td className="text-sc-text-secondary">{session.customerName}</td>
                  <td className="text-sc-text-secondary">{session.technicianName}</td>
                  <td className="text-sc-text-muted text-sm">
                    {formatRelativeTime(session.startedAt)}
                  </td>
                  <td className="text-sc-text-muted text-sm">
                    {formatDuration(session.duration)}
                  </td>
                  <td>{getStatusBadge(session.status)}</td>
                  <td className="text-sc-text-muted text-sm max-w-[200px] truncate">
                    {session.notes || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {completedSessions.length === 0 && (
          <div className="p-12 text-center">
            <History className="w-12 h-12 text-sc-text-muted mx-auto mb-4" />
            <h3 className="text-lg font-medium text-sc-text-primary mb-2">
              No session history
            </h3>
            <p className="text-sc-text-muted">
              Completed sessions will appear here
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
